//
import type { AxiosCollection, AxiosErrors } from '@/types/Components/Response'
import type { AssignmentResponseData } from '@/types/Models/Assignment'
import type { AssignmentStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { catchErrors } from '@/services/Helpers'
import { isSameObject } from '@/services/Utils'
import AssignmentProxy from '@/proxies/AssignmentProxy'
import AssignmentsListTransformer from '@/transformers/AssignmentsListTransformer'

const createDefaultState = (): AssignmentStoreState => ({
  version: '',
  itemsVersion: '',
  assignments: [],
  assignmentsFilter: {},
  assignmentsLoading: false,
  assignmentsCount: 0,
})

export const useAssignmentStore = defineStore('assignment', {
  state: (): AssignmentStoreState => createDefaultState(),
  actions: {
    refresh() {
      this.version = new Date().toISOString()
    },
    refreshItems() {
      this.itemsVersion = new Date().toISOString()
    },
    unmount() {
      Object.assign(this, createDefaultState())
      this.refresh()
      this.refreshItems()
    },
    fetch() {
      this.assignmentsLoading = true

      new AssignmentProxy(this.assignmentsFilter || undefined)
        .all()
        .then((response: AxiosCollection<AssignmentResponseData>) => {
          this.assignments = AssignmentsListTransformer.fetchCollection(response.data)
          this.assignmentsCount = response.count || 0
        })
        .catch((error: AxiosErrors) => {
          this.assignmentsCount = 0
          this.assignments = []
          catchErrors(error)
        })
        .finally(() => {
          this.assignmentsLoading = false
        })
    },
    async setAssignmentsFilter(filter: Record<string, string> | null) {
      if (isSameObject(filter, this.assignmentsFilter)) return

      this.assignmentsFilter = filter
      return this.fetch()
    },
    async updateFilterValue({ key, value }: { key: string; value: string }) {
      const filter = {
        ...(this.assignmentsFilter ?? {}),
        [key]: value,
      }

      if (isSameObject(filter, this.assignmentsFilter)) return

      this.assignmentsFilter = filter
      return this.fetch()
    },
    async updateAssignmentsFilter(partialFilter: Record<string, string | null>) {
      const filter = {
        ...(this.assignmentsFilter ?? {}),
        ...partialFilter,
      }

      if (isSameObject(filter, this.assignmentsFilter)) return

      this.assignmentsFilter = filter
      return this.fetch()
    },
  },
})

export type AssignmentStore = ReturnType<typeof useAssignmentStore>
